import { useEffect, useMemo, useState } from "react"
import { TrendingUp } from "@mui/icons-material"
import { Box, useTheme } from "@mui/material"
import intl from "src/internationalization/intl"
import {
    CircularProgress,
    Grid,
    MultiSeriesTrendChart,
    PERIOD_FILTERS,
    PeriodRangeSelector,
    Section,
    TabFilters,
    Typography,
} from "src/lib"
import type { MultiSeriesTrendChartSeries } from "src/lib/components/complex/MultiSeriesTrendChart"
import type { Filter } from "src/lib/components/basics/TabFilters/types"
import {
    formatCompactCurrencyBRL,
    formatCompactNumberPtBR,
    formatCurrencyBRL,
    formatNumberPtBR,
    formatPercentPtBR,
} from "src/lib/utils/numberFormat"
import { computePeriodRange, PeriodPresetId, toDateInputValue } from "src/lib/utils/periodRange"
import { useDrawerStore } from "src/store/drawerStore"
import { useExecutionPerformanceStore } from "src/store/executionPerformanceStore"
import { useMessageStore } from "src/store/messageStore"
import { fetchExecutionPerformance } from "src/services/api/executionPerformance"
import type { ExecutionPerformanceUnit } from "src/services/api/executionPerformance/types"
import { PerformanceScatterPlot } from "./PerformanceScatterPlot"
import { OverviewMetricCard } from "./OverviewMetricCard"

const UNIT_FILTERS: Filter[] = [
    { id: "volume", value: "volume", label: intl("unitVolume") },
    { id: "clients", value: "clients", label: intl("unitClients") },
]

/**
 * Base de liquidação do numerador. Não é preferência cosmética: movimento pedido e movimento
 * liquidado são números bem diferentes (em base de referência, R$ 162 mil liquidados contra
 * R$ 1,3 mi ainda em trânsito no mesmo período). O default é o conservador — só liquidado — e
 * incluir o em trânsito é escolha explícita de quem está olhando.
 */
const BASIS_FILTERS: Filter[] = [
    { id: "settled", value: "settled", label: intl("basisSettled") },
    { id: "withPending", value: "withPending", label: intl("basisWithPending") },
]

const safeRatio = (executed: number, total: number): number => (total > 0 ? (executed / total) * 100 : 0)

/**
 * Visão consolidada da execução das oportunidades na janela selecionada: quanto do volume (ou de
 * quantos clientes) oferecido em oportunidades virou movimentação de fato, com a curva de
 * atingimento no período e o recorte por assessor e por oportunidade.
 *
 * O denominador é o potencial *integral* das oportunidades cuja validade cruza a janela, sem
 * pró-rata — leitura de atingimento de meta, não de fluxo. Recorte curto no meio de uma
 * oportunidade longa mostra % baixo por construção.
 */
const ExecutionPerformanceSection = () => {
    const theme = useTheme() as any
    const executionPerformanceData = useExecutionPerformanceStore((s) => s.executionPerformanceData)
    const setExecutionPerformanceData = useExecutionPerformanceStore((s) => s.setExecutionPerformanceData)
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const setJsonContext = useMessageStore((s: any) => s.setJsonContext)
    const setActualContext = useMessageStore((s: any) => s.setActualContext)
    const openDrawer = useDrawerStore((s: any) => s.openDrawer)

    const [selectedPeriod, setSelectedPeriod] = useState<Filter>(PERIOD_FILTERS[0])
    const [customStart, setCustomStart] = useState(() => toDateInputValue(computePeriodRange("mtd").start))
    const [customEnd, setCustomEnd] = useState(() => toDateInputValue(computePeriodRange("mtd").end))
    const [selectedUnit, setSelectedUnit] = useState<Filter>(UNIT_FILTERS[0])
    const [selectedBasis, setSelectedBasis] = useState<Filter>(BASIS_FILTERS[0])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const unit = selectedUnit.id as ExecutionPerformanceUnit
    const includePending = selectedBasis.id === "withPending"

    const range = useMemo(
        () => computePeriodRange(selectedPeriod.id as PeriodPresetId, { start: customStart, end: customEnd }),
        [selectedPeriod, customStart, customEnd]
    )

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            setIsLoading(true)
            setError(null)
            try {
                const execution = await fetchExecutionPerformance({
                    start: range.start.toISOString(),
                    end: range.end.toISOString(),
                    includePending,
                })
                if (!cancelled) {
                    setExecutionPerformanceData(execution)
                }
            } catch (err) {
                console.error("Erro ao buscar a visão consolidada da execução", err)
                if (!cancelled) {
                    setError(intl("executionPerformanceLoadError"))
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }

        load()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.start.getTime(), range.end.getTime(), includePending])

    const handleClickAIButton = () => {
        if (actualContext !== "executionPerformance") {
            try {
                setActualContext("executionPerformance")
                setJsonContext({ execution: executionPerformanceData })
            } catch (err) {
                console.error("Error setting execution performance context:", err)
            }
        }
        openDrawer()
    }

    const formatUnitValue = (value: number) =>
        unit === "volume" ? formatCompactCurrencyBRL(value) : formatCompactNumberPtBR(value)

    // Todas as leituras do payload são defensivas de propósito: em design mode uma rota sem mock
    // resolve para `{}`, e uma resposta truncada não deve derrubar a aba inteira do gestor.
    const series = useMemo(
        () =>
            (unit === "volume"
                ? executionPerformanceData?.seriesByVolume
                : executionPerformanceData?.seriesByClients) ?? [],
        [executionPerformanceData, unit]
    )

    const { ratioNow, deltaPts } = useMemo(() => {
        const current = executionPerformanceData?.current
        const previous = executionPerformanceData?.previous
        if (!current || !previous) {
            return { ratioNow: 0, deltaPts: null as number | null }
        }
        const now = safeRatio(
            unit === "volume" ? current.executedVolume : current.executedClients,
            unit === "volume" ? current.totalVolume : current.totalClients
        )
        const prev = safeRatio(
            unit === "volume" ? previous.executedVolume : previous.executedClients,
            unit === "volume" ? previous.totalVolume : previous.totalClients
        )
        return { ratioNow: now, deltaPts: now - prev }
    }, [executionPerformanceData, unit])

    // x = performance de execução (%), y = volume/clientes executados (valor absoluto).
    const byAdvisor = useMemo(
        () =>
            executionPerformanceData?.byAdvisor?.map((e) => ({
                id: e.id,
                label: e.label || intl("executionUnassignedAdvisor"),
                x: unit === "volume" ? safeRatio(e.executedVolume, e.totalVolume) : safeRatio(e.executedClients, e.totalClients),
                y: unit === "volume" ? e.executedVolume : e.executedClients,
            })) ?? [],
        [executionPerformanceData, unit]
    )

    const byOpportunity = useMemo(
        () =>
            executionPerformanceData?.byOpportunity?.map((e) => ({
                id: e.id,
                label: e.label || intl("executionUntitledOpportunity"),
                x: unit === "volume" ? safeRatio(e.executedVolume, e.totalVolume) : safeRatio(e.executedClients, e.totalClients),
                y: unit === "volume" ? e.executedVolume : e.executedClients,
            })) ?? [],
        [executionPerformanceData, unit]
    )

    const executionChartSeries: MultiSeriesTrendChartSeries[] = useMemo(
        () => [
            {
                id: "consolidated",
                label: intl("executionAverageLabel"),
                color: theme.palette.secondary.main,
                dashed: true,
                points: series,
            },
        ],
        [series, theme]
    )

    const pendingText = useMemo(() => {
        const current = executionPerformanceData?.current
        if (!current) return null
        const value = unit === "volume" ? current.pendingVolume : current.pendingClients
        if (!value) return null
        return unit === "volume" ? formatCurrencyBRL(value) : formatNumberPtBR(value)
    }, [executionPerformanceData, unit])

    return (
        <Section
            title={intl("executionPerformanceOverviewTitle")}
            subtitle={intl("executionPerformanceOverviewSubtitle")}
            icon={<TrendingUp />}
            onClickAIButton={handleClickAIButton}
            headerComponents={
                <PeriodRangeSelector
                    selectedPeriod={selectedPeriod}
                    onPeriodChange={setSelectedPeriod}
                    customStart={customStart}
                    customEnd={customEnd}
                    onCustomStartChange={setCustomStart}
                    onCustomEndChange={setCustomEnd}
                />
            }
        >
            {!executionPerformanceData ? (
                <Grid container justifyContent="center" sx={{ mt: 4, mb: 2, width: "100%" }}>
                    <CircularProgress color="primary" />
                </Grid>
            ) : (
                <Box sx={{ width: "100%", opacity: isLoading ? 0.5 : 1, transition: "opacity 150ms ease" }}>
                    {error && (
                        <Typography variant="caption" sx={{ display: "block", mb: 1, color: "accentError.main" }}>
                            {error}
                        </Typography>
                    )}

                    {executionPerformanceData.warnings?.map((warning) => (
                        <Typography
                            key={warning}
                            variant="caption"
                            sx={{ display: "block", mb: 1, color: "text.secondary" }}
                        >
                            {warning}
                        </Typography>
                    ))}

                    <Grid container gap={1.5} sx={{ mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <TabFilters filters={UNIT_FILTERS} selectedFilter={selectedUnit} onFilterChange={setSelectedUnit} />
                        <TabFilters filters={BASIS_FILTERS} selectedFilter={selectedBasis} onFilterChange={setSelectedBasis} />
                    </Grid>

                    <Grid container gap={3} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
                        <Grid container gap={2} sx={{ flex: "1 1 280px" }}>
                            <OverviewMetricCard
                                icon={<TrendingUp fontSize="small" />}
                                accentSlot="secondary"
                                accentColor={theme.palette.secondary.main}
                                label={intl("executionCombinedCardLabel")}
                                valueText={formatPercentPtBR(ratioNow)}
                                deltaValue={deltaPts}
                                deltaFormat={(v) => `${formatNumberPtBR(v)} p.p.`}
                                favorableDirection="up"
                                sparklinePoints={series}
                                selected
                                onSelect={() => undefined}
                                footer={
                                    pendingText ? (
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            {intl("executionPendingHint", { values: { value: pendingText } })}
                                        </Typography>
                                    ) : undefined
                                }
                            />
                        </Grid>

                        <Grid sx={{ flex: "1 1 520px", minWidth: 0 }}>
                            <Typography variant="body2" color="secondary" sx={{ display: "block", mb: 1 }}>
                                {intl("advisorCurvesTitle")}
                            </Typography>
                            {series.length > 0 ? (
                                <MultiSeriesTrendChart series={executionChartSeries} format={formatPercentPtBR} />
                            ) : (
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {intl("executionNoDataInWindow")}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>

                    <Grid container gap={3} sx={{ mt: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
                        <Grid sx={{ flex: "1 1 420px", minWidth: 0 }}>
                            <PerformanceScatterPlot
                                title={intl("executionByAdvisorTitle")}
                                entities={byAdvisor}
                                xFormat={formatPercentPtBR}
                                yFormat={formatUnitValue}
                                xAxisLabel={intl("scatterXAxisExecutionLabel")}
                                yAxisLabel={
                                    unit === "volume" ? intl("scatterYAxisVolumeLabel") : intl("scatterYAxisClientsLabel")
                                }
                            />
                        </Grid>
                        <Grid sx={{ flex: "1 1 420px", minWidth: 0 }}>
                            <PerformanceScatterPlot
                                title={intl("executionByOpportunityTitle")}
                                entities={byOpportunity}
                                xFormat={formatPercentPtBR}
                                yFormat={formatUnitValue}
                                xAxisLabel={intl("scatterXAxisExecutionLabel")}
                                yAxisLabel={
                                    unit === "volume" ? intl("scatterYAxisVolumeLabel") : intl("scatterYAxisClientsLabel")
                                }
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Section>
    )
}

export { ExecutionPerformanceSection }
